import React, { useEffect, useMemo, useCallback, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { MENU_ITEMS } from './config';
import useDashboardState from './useDashboardState';
import CompanyVerificationPage from './company/CompanyVerificationPage';
import PublicationSection from './publications/PublicationSection';
import UnitsSection from './units/UnitsSection';
import SupportTicketsSection from './support/SupportTicketsSection';
import SupportCreateTicketSection from './support/SupportCreateTicketSection';
import SolicitudesSection from './solicitudes/SolicitudesSection';
import FeedSection from './feed/FeedSection';
import MarketplaceSection from './marketplace/MarketplaceSection';
import CalendarSection from './calendar/CalendarSection';
import BookingSection from './booking/BookingSection';
import CustomerReservationsSection from './booking/CustomerReservationsSection';
import PromoteSection from './promote/PromoteSection';
import SubscriptionsSection from './subscriptions/SubscriptionsSection';
import StatisticsReportsSection from './statistics/StatisticsReportsSection';
import StatisticsClientsSection from './statistics/StatisticsClientsSection';
import StatisticsProvidersSection from './statistics/StatisticsProvidersSection';
import NotificationsSection from './notifications/NotificationsSection';
import './Dashboard.css';
import DashboardSidebar from './DashboardSidebar';
import HelpCenterTabs from '../../components/HelpCenterTabs';
import OperationsTabs from '../../components/OperationsTabs';
import AnalyticsTabs from '../../components/AnalyticsTabs';
import PromoteTabs from '../../components/PromoteTabs';
import { getCompanyPortalPath } from '../../constants/companyRoutes';
import {
  listCompanyNotifications,
  markNotificationsRead,
} from '../../services/notificationsApi';
import { listPublications } from '../../services/publicationsApi';
import { listUnits } from '../../services/unitsApi';

const buildInitials = text => {
  if (!text) return 'U';
  return text
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map(part => part.charAt(0).toUpperCase())
    .join('') || 'U';
};

const MEMBERSHIP_PLAN_LABELS = {
  free: 'Free',
  founder: 'Fundador',
  pro: 'Pro',
  premium: 'Premium',
  elite: 'Elite',
};

const MEMBERSHIP_PLAN_COLORS = {
  free: '#94a3b8',
  founder: '#22d3ee',
  pro: '#2563eb',
  premium: '#f59e0b',
  elite: '#8b5cf6',
};

const SECTION_PATH_ALIASES = {
  feed: 'feed',
  publicaciones: 'publicaciones',
  productos: 'publicaciones',
  unidades: 'unidades',
  flotilla: 'unidades',
  fleet: 'unidades',
  reservas: 'reservas',
  'reservas-cliente': 'reservas-cliente',
  'mis-reservas': 'reservas-cliente',
  solicitudes: 'solicitudes',
  calendario: 'calendario',
  empresa: 'empresa',
  'empresa-verificacion': 'empresa-verificacion',
  support: 'support-tickets',
  soporte: 'support-tickets',
  'support-tickets': 'support-tickets',
  'support-new': 'support-new',
  notificaciones: 'notificaciones',
  marketplace: 'marketplace',
  suscripciones: 'suscripciones',
  planes: 'suscripciones',
  promocionar: 'promocionar',
  promo: 'promocionar',
  news: 'news',
  admin: 'admin',
  estadisticas: 'estadisticas-reportes',
  reportes: 'estadisticas-reportes',
  'estadisticas-reportes': 'estadisticas-reportes',
  'estadisticas-clientes': 'estadisticas-clientes',
  clientes: 'estadisticas-clientes',
  'estadisticas-proveedores': 'estadisticas-proveedores',
  proveedores: 'estadisticas-proveedores',
  analytics: 'estadisticas-reportes',
};

const DEPRECATED_PROFILE_SECTIONS = ['profile', 'perfil'];

const OPERATIONS_SECTIONS = new Set([
  'publicaciones',
  'reservas',
  'reservas-cliente',
  'unidades',
  'solicitudes',
  'calendario',
]);
const ANALYTICS_SECTIONS = new Set([
  'estadisticas-reportes',
  'estadisticas-clientes',
  'estadisticas-proveedores',
]);
const PROMOTE_SECTIONS = new Set(['promocionar', 'suscripciones']);

export default function Dashboard({
  user,
  handleSignOut,
  onSupportAssistantContextChange,
  companyNotifications,
  onCompanyNotificationsChange,
}) {
  const {
    section,
    setSection,
    isAdmin,
    isClient,
    empresaId,
    empresaValues,
    canEditCompany,
    empresaUsers,
    empresaProfileViews,
    empresaProfileViewsTotal,
    empresaUserRequests,
    empresaLoading,
    userProfileViews,
    userProfileViewsTotal,
    publicationViewDetails,
    publicationViewTotal,
    profileUser,
    profileName,
    profileLocation,
    profileAbout,
    profileAvatarUrl,
    profileHeroUrl,
  } = useDashboardState(user);

  const location = useLocation();
  const navigate = useNavigate();
  const isProviderLike = !isAdmin && !isClient;
  const [sidebarMetrics, setSidebarMetrics] = useState({ publications: 0, units: 0 });
  const teamMembersCount = Array.isArray(empresaUsers) ? empresaUsers.length : 0;

  const userId = user?.id || '';

  const isCompanyOwner = useMemo(() => {
    if (!empresaId || !userId) return false;
    return empresaValues?.ownerId && String(empresaValues.ownerId) === String(userId);
  }, [empresaId, empresaValues?.ownerId, userId]);

  const isCompanyMember = useMemo(() => {
    if (!empresaId || !userId) return false;
    if (isCompanyOwner) return true;
    return (
      Array.isArray(empresaUsers) &&
      empresaUsers.some(member => member?.id && String(member.id) === String(userId))
    );
  }, [empresaId, empresaUsers, isCompanyOwner, userId]);

  const companyViewCount = useMemo(() => {
    const candidates = [
      empresaValues?.profileViews,
      empresaValues?.views,
      empresaValues?.viewCount,
      empresaValues?.view_count,
      empresaValues?.totalViews,
    ];
    const numericCandidate = candidates
      .map(value => Number(value))
      .find(value => Number.isFinite(value));
    if (!Number.isFinite(numericCandidate)) {
      return 0;
    }
    return Math.max(0, Math.round(numericCandidate));
  }, [
    empresaValues?.profileViews,
    empresaValues?.views,
    empresaValues?.viewCount,
    empresaValues?.view_count,
    empresaValues?.totalViews,
  ]);

  const accountCreditBalance = useMemo(() => {
    const userCredits = Number(user?.credits);
    if (Number.isFinite(userCredits)) {
      return Math.max(0, userCredits);
    }
    const companyCredits = Number(empresaValues?.ppcCredits);
    if (Number.isFinite(companyCredits)) {
      return Math.max(0, companyCredits);
    }
    return 0;
  }, [user?.credits, empresaValues?.ppcCredits]);

  const membershipSlugNormalized = String(empresaValues?.membershipPlan || '').toLowerCase();
  const canViewVisitorDetails =
    isCompanyMember && ['pro', 'premium', 'elite'].includes(membershipSlugNormalized);

  const sanitizeNotification = useCallback(
    notification => {
      if (!notification) {
        return notification;
      }

      if (notification.type === 'profile_view') {
        if (canViewVisitorDetails) {
          return notification;
        }
        return {
          ...notification,
          message: 'Un usuario visitó el perfil de tu empresa.',
          data: {
            ...notification.data,
            viewerId: null,
            viewerLabel: notification?.data?.viewerId ? 'Usuario registrado' : 'Invitado',
            viewerUsername: '',
            viewerUrl: '',
          },
        };
      }

      if (notification.type === 'publication_view') {
        if (canViewVisitorDetails) {
          return notification;
        }
        return {
          ...notification,
          message: 'Un usuario consultó una de tus publicaciones.',
          data: {
            ...notification.data,
            viewerId: null,
            viewerLabel: 'Actividad protegida',
            viewerUsername: '',
            viewerUrl: '',
          },
        };
      }

      return notification;
    },
    [canViewVisitorDetails]
  );

  const accessibleItems = useMemo(() => {
    const roleKey = isAdmin ? 'admin' : 'user';
    return MENU_ITEMS.filter(item => {
      if (!item.roles.includes(roleKey)) {
        return false;
      }
      if (Array.isArray(item.audiences) && item.audiences.length) {
        if (!isAdmin) {
          if (item.audiences.includes('client') && !isClient) {
            return false;
          }
          if (item.audiences.includes('provider') && !isProviderLike) {
            return false;
          }
        }
      }
      return true;
    });
  }, [isAdmin, isClient, isProviderLike]);

  useEffect(() => {
    if (!empresaId) {
      setSidebarMetrics({ publications: 0, units: 0 });
      return;
    }

    let canceled = false;
    (async () => {
      try {
        const [publications, units] = await Promise.all([
          listPublications({ companyId: empresaId }),
          listUnits({ companyId: empresaId }),
        ]);
        if (canceled) return;
        setSidebarMetrics({
          publications: Array.isArray(publications) ? publications.length : 0,
          units: Array.isArray(units) ? units.length : 0,
        });
      } catch (error) {
        if (!canceled) {
          setSidebarMetrics({ publications: 0, units: 0 });
        }
      }
    })();

    return () => {
      canceled = true;
    };
  }, [empresaId]);

  const visibleItems = useMemo(
    () => accessibleItems.filter(item => !item.hiddenInSidebar),
    [accessibleItems]
  );

  const sidebarNavSections = useMemo(() => {
    if (!visibleItems.length) {
      return [];
    }
    const sections = [];
    const sectionMap = new Map();
    visibleItems.forEach(item => {
      const label = item.section || 'General';
      if (!sectionMap.has(label)) {
        const group = { label, items: [] };
        sectionMap.set(label, group);
        sections.push(group);
      }
      sectionMap.get(label).items.push({
        key: item.key,
        label: item.label,
        icon: item.icon,
      });
    });
    return sections;
  }, [visibleItems]);

  const fallbackSection = useMemo(() => {
    return visibleItems[0]?.key || accessibleItems[0]?.key || 'feed';
  }, [visibleItems, accessibleItems]);

  const membershipSlug = useMemo(() => {
    const raw = String(empresaValues?.membershipPlan || '').toLowerCase();
    if (!raw) return '';
    if (raw.includes('elite')) return 'elite';
    if (raw.includes('premium')) return 'premium';
    if (raw.includes('pro')) return 'pro';
    if (raw.includes('founder')) return 'founder';
    if (raw.includes('free')) return 'free';
    return raw;
  }, [empresaValues?.membershipPlan]);

  const supportCompanySlug = empresaValues?.slug || '';
  const supportCompanyName = empresaValues?.name || '';
  const supportUserUsername = user?.username || '';

  const sectionParam = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get('section') || '';
  }, [location.search]);

  const dashboardPathSegment = useMemo(() => {
    if (!location?.pathname) return '';
    const segments = location.pathname.split('/').filter(Boolean);
    if (segments[0] !== 'dashboard' || segments.length < 2) {
      return '';
    }
    return segments[1]?.toLowerCase?.() || '';
  }, [location.pathname]);

  const pathSection = useMemo(() => {
    if (!dashboardPathSegment) return '';
    return SECTION_PATH_ALIASES[dashboardPathSegment] || '';
  }, [dashboardPathSegment]);

  const isLegacyWelcomeRoute =
    sectionParam === 'welcome-client' || sectionParam === 'welcome-provider';

  const routeSection = useMemo(() => {
    const normalizedSectionParam = sectionParam
      ? SECTION_PATH_ALIASES[sectionParam] || sectionParam
      : '';
    if (normalizedSectionParam) {
      const resolvedFromQuery = accessibleItems.find(item => item.key === normalizedSectionParam)?.key;
      if (resolvedFromQuery) {
        return resolvedFromQuery;
      }
    }
    if (pathSection) {
      return accessibleItems.find(item => item.key === pathSection)?.key || '';
    }
    return '';
  }, [sectionParam, pathSection, accessibleItems]);

  const currentSection =
    routeSection ||
    (accessibleItems.find(item => item.key === section)?.key ?? fallbackSection);

  useEffect(() => {
    if (!isLegacyWelcomeRoute) return;
    navigate('/welcome', { replace: true });
  }, [isLegacyWelcomeRoute, navigate]);

  useEffect(() => {
    const normalizedSection = (sectionParam || '').trim().toLowerCase();
    const normalizedPathSection = (pathSection || '').trim().toLowerCase();
    if (normalizedSection === 'empresa' || normalizedPathSection === 'empresa') {
      navigate(getCompanyPortalPath(empresaValues), { replace: true });
    }
  }, [sectionParam, pathSection, navigate, empresaValues]);

  useEffect(() => {
    const normalizedSection = (sectionParam || '').trim().toLowerCase();
    const normalizedPathSegment = (dashboardPathSegment || '').trim().toLowerCase();
    if (
      normalizedSection === 'solicitudes-mapa' ||
      normalizedSection === 'mapa' ||
      normalizedPathSegment === 'solicitudes-mapa' ||
      normalizedPathSegment === 'mapa'
    ) {
      navigate('/marketplace?segment=requests', { replace: true });
    }
  }, [sectionParam, dashboardPathSegment, navigate]);

  useEffect(() => {
    if (section !== currentSection) {
      setSection(currentSection);
    }
  }, [section, currentSection, setSection]);

  useEffect(() => {
    if (!currentSection || isLegacyWelcomeRoute) return;
    const params = new URLSearchParams(location.search);
    if (params.get('section') === currentSection) return;
    params.set('section', currentSection);
    navigate(`/dashboard?${params.toString()}`, {
      replace: true,
    });
  }, [currentSection, location.search, navigate, isLegacyWelcomeRoute]);

  useEffect(() => {
    if (typeof onSupportAssistantContextChange !== 'function') {
      return;
    }
    onSupportAssistantContextChange({
      companySlug: supportCompanySlug,
      companyName: supportCompanyName,
      userUsername: supportUserUsername,
    });
  }, [
    onSupportAssistantContextChange,
    supportCompanySlug,
    supportCompanyName,
    supportUserUsername,
  ]);

  useEffect(() => {
    if (typeof onSupportAssistantContextChange !== 'function') {
      return undefined;
    }
    return () => {
      onSupportAssistantContextChange(null);
    };
  }, [onSupportAssistantContextChange]);

  useEffect(() => {
    if (!empresaId) {
      if (typeof onCompanyNotificationsChange === 'function') {
        onCompanyNotificationsChange([]);
      }
      return undefined;
    }

    let canceled = false;
    let pollTimer = null;

    const fetchAndSyncNotifications = async () => {
      try {
        const items = await listCompanyNotifications({ companyId: empresaId, limit: 50 });
        if (canceled || typeof onCompanyNotificationsChange !== 'function') {
          return;
        }
        const filtered = Array.isArray(items)
          ? items
              .filter(item => {
                const targetUserId = item?.data?.targetUserId;
                if (targetUserId && userId && targetUserId !== userId) {
                  return false;
                }
                return true;
              })
              .map(sanitizeNotification)
          : [];
        onCompanyNotificationsChange(filtered);
      } catch (error) {
        if (!canceled) {
          console.warn('No se pudieron cargar las notificaciones de la empresa.', error);
        }
      }
    };

    fetchAndSyncNotifications();

    if (typeof window !== 'undefined') {
      pollTimer = window.setInterval(fetchAndSyncNotifications, 45000);
    }

    return () => {
      canceled = true;
      if (pollTimer) {
        window.clearInterval(pollTimer);
      }
    };
  }, [empresaId, onCompanyNotificationsChange, sanitizeNotification, userId]);

  useEffect(() => {
    if (
      currentSection !== 'reservas' ||
      !Array.isArray(companyNotifications) ||
      !companyNotifications.length
    ) {
      return;
    }

    const unreadIds = companyNotifications
      .filter(item => item && item.type === 'reservation' && !item.read)
      .map(item => item.id);

    if (!unreadIds.length) return;

    markNotificationsRead(unreadIds).catch(error => {
      console.warn('No se pudieron marcar como leídas las notificaciones.', error);
    });

    if (typeof onCompanyNotificationsChange === 'function') {
      onCompanyNotificationsChange(prev => {
        if (!Array.isArray(prev) || !prev.length) return prev;
        let changed = false;
        const next = prev.map(item => {
          if (unreadIds.includes(item.id) && !item.read) {
            changed = true;
            return { ...item, read: true };
          }
          return item;
        });
        return changed ? next : prev;
      });
    }
  }, [currentSection, companyNotifications, onCompanyNotificationsChange]);

  const handleSectionChange = key => {
    if (!key) return;
    if (key === 'empresa') {
      navigate(getCompanyPortalPath(empresaValues));
      return;
    }
    const allowed = accessibleItems.find(item => item.key === key);
    if (!allowed) return;
    const params = new URLSearchParams(location.search);
    params.set('section', key);
    params.delete('tab');
    params.delete('requests');
    navigate(`/dashboard?${params.toString()}`);
  };

  const activeLabel =
    accessibleItems.find(item => item.key === currentSection)?.label ??
    accessibleItems.find(item => item.key === fallbackSection)?.label ??
    'Dashboard';

  const planSlug =
    membershipSlug ||
    String(empresaValues?.membershipPlan || '').trim().toLowerCase();
  const planLabel =
    MEMBERSHIP_PLAN_LABELS[planSlug] ||
    (planSlug
      ? planSlug.charAt(0).toUpperCase() + planSlug.slice(1)
      : 'Plan sin asignar');
  const planColor = planSlug
    ? MEMBERSHIP_PLAN_COLORS[planSlug] || '#2563eb'
    : '#94a3b8';
  const profileIdentifierCandidates = [
    typeof profileUser?.username === 'string' ? profileUser.username.trim() : '',
    typeof user?.username === 'string' ? user.username.trim() : '',
    typeof profileUser?.id === 'string' ? profileUser.id.trim() : '',
    typeof user?.id === 'string' ? user.id.trim() : '',
  ].filter(Boolean);
  const sidebarProfileHref = profileIdentifierCandidates.length
    ? `/usuario/${encodeURIComponent(profileIdentifierCandidates[0])}`
    : '';
  const accountNameCandidates = [
    typeof profileName === 'string' ? profileName.trim() : '',
    typeof profileUser?.name === 'string' ? profileUser.name.trim() : '',
    typeof user?.displayName === 'string' ? user.displayName.trim() : '',
    typeof user?.name === 'string' ? user.name.trim() : '',
    typeof user?.email === 'string' ? user.email.trim() : '',
  ].filter(Boolean);
  const sidebarAccountName = accountNameCandidates[0] || 'Mi cuenta';
  const emailCandidates = [
    typeof user?.email === 'string' ? user.email.trim() : '',
    typeof profileUser?.email === 'string' ? profileUser.email.trim() : '',
    typeof empresaValues?.contactEmail === 'string'
      ? empresaValues.contactEmail.trim()
      : '',
  ].filter(Boolean);
  const sidebarAccountEmail = emailCandidates[0] || '';
  const sidebarHeadlineCandidates = [
    typeof profileUser?.title === 'string' ? profileUser.title.trim() : '',
    typeof profileAbout === 'string' ? profileAbout.trim() : '',
  ].filter(Boolean);
  const sidebarHeadline = sidebarHeadlineCandidates[0] || '';
  const sidebarLocation = typeof profileLocation === 'string' ? profileLocation.trim() : '';
  const sidebarCompanyName = typeof empresaValues?.name === 'string' ? empresaValues.name.trim() : '';
  const sidebarCoverImage = profileHeroUrl || empresaValues?.heroUrl || '';
  const unreadNotificationsCount = useMemo(
    () =>
      Array.isArray(companyNotifications)
        ? companyNotifications.filter(item => item && !item.read).length
        : 0,
    [companyNotifications]
  );
  const activeRequestsCount = useMemo(() => {
    if (!Array.isArray(empresaUserRequests)) return 0;
    return empresaUserRequests.filter(request => {
      const status = String(request?.status || '').toLowerCase();
      if (!status) return true;
      return !['approved', 'rejected', 'closed', 'completed', 'resolved'].some(value =>
        status.includes(value)
      );
    }).length;
  }, [empresaUserRequests]);
  const sidebarStats = [
    {
      label: 'Publicaciones',
      value: sidebarMetrics.publications,
      href: '/dashboard?section=publicaciones',
    },
    {
      label: 'Unidades registradas',
      value: sidebarMetrics.units,
      href: '/dashboard?section=unidades',
    },
    {
      label: 'Solicitudes activas',
      value: activeRequestsCount,
      href: '/dashboard?section=solicitudes',
    },
    {
      label: 'Notificaciones',
      value: unreadNotificationsCount,
      href: '/dashboard?section=feed',
    },
  ];
  const sectionIsAccessible = key =>
    accessibleItems.some(item => item.key === key);
  const sidebarAccountActions = [];
  sidebarAccountActions.push({
    key: 'empresa',
    label: 'Ver empresa',
    onSelect: () => navigate(getCompanyPortalPath(empresaValues)),
  });
  if (sectionIsAccessible('suscripciones')) {
    sidebarAccountActions.push({
      key: 'suscripciones',
      label: 'Ver planes',
      onSelect: () => handleSectionChange('suscripciones'),
    });
  }
  if (typeof handleSignOut === 'function') {
    sidebarAccountActions.push({
      key: 'signout',
      label: 'Cerrar sesion',
      onSelect: handleSignOut,
      tone: 'danger',
    });
  }
  const sidebarAccountCard = {
    name: sidebarAccountName,
    email: sidebarAccountEmail,
    avatar: profileAvatarUrl || undefined,
    headline: sidebarHeadline,
    location: sidebarLocation,
    companyName: sidebarCompanyName,
    coverImage: sidebarCoverImage,
    stats: sidebarStats,
    planLabel,
    planColor,
    initials: buildInitials(sidebarAccountName || sidebarAccountEmail),
    actions: sidebarAccountActions,
    subscriptionStatus: planLabel,
    subscriptionPlan: planSlug,
    verificationCTA: empresaValues ? !empresaValues.isVerified : false,
    verificationHref: '/dashboard?section=empresa-verificacion',
    profileHref: sidebarProfileHref,
  };

  useEffect(() => {
    const normalizedSectionParam = (sectionParam || '').trim().toLowerCase();
    const normalizedPathSegment = dashboardPathSegment.trim().toLowerCase();
    const matchesDeprecatedProfileRoute =
      DEPRECATED_PROFILE_SECTIONS.includes(normalizedSectionParam) ||
      (!normalizedSectionParam &&
        normalizedPathSegment &&
        DEPRECATED_PROFILE_SECTIONS.includes(normalizedPathSegment));
    if (!matchesDeprecatedProfileRoute) {
      return;
    }
    const target = sidebarProfileHref || '/dashboard';
    navigate(target, { replace: true });
  }, [sectionParam, dashboardPathSegment, sidebarProfileHref, navigate]);

  let content;
  if (currentSection === 'empresa-verificacion') {
    content = (
      <CompanyVerificationPage
        company={empresaValues}
        canUploadDocuments={canEditCompany}
        loading={empresaLoading}
      />
    );
  } else if (currentSection === 'publicaciones' || currentSection === 'productos') {
    content = (
      <PublicationSection
        user={user}
        empresaId={empresaId}
        empresaCategory={empresaValues?.category}
        empresaCategories={empresaValues?.categories}
        empresaPlan={empresaValues?.membershipPlan}
      />
    );
  } else if (currentSection === 'unidades') {
    content = <UnitsSection user={user} empresaId={empresaId} />;
  } else if (currentSection === 'solicitudes') {
    content = (
      <SolicitudesSection
        user={user}
        empresaId={empresaId}
        empresaValues={empresaValues}
      />
    );
  } else if (currentSection === 'calendario') {
    content = (
      <CalendarSection
        user={user}
        empresaId={empresaId}
        empresaValues={empresaValues}
      />
    );
  } else if (currentSection === 'reservas') {
    content = (
      <BookingSection
        user={user}
        empresaId={empresaId}
      />
    );
  } else if (currentSection === 'reservas-cliente') {
    content = <CustomerReservationsSection user={user} />;
  } else if (currentSection === 'marketplace') {
    content = <MarketplaceSection />;
  } else if (currentSection === 'promocionar') {
    content = (
      <PromoteSection
        empresaId={empresaId}
        empresaName={empresaValues?.name}
        companyCredits={accountCreditBalance}
        leadCredits={empresaValues?.leadCredits}
      />
    );
  } else if (currentSection === 'suscripciones') {
    const showUpgradeModal = !empresaLoading && ['free', 'founder'].includes(membershipSlug);
    content = (
      <SubscriptionsSection
        empresaId={empresaId}
        empresaName={empresaValues.name}
        currentPlanId={empresaValues.membershipPlan}
        publicationLimit={empresaValues.publicationLimit}
        userLimit={empresaValues.userLimit}
        ppcCredits={empresaValues.ppcCredits}
        leadCredits={empresaValues.leadCredits}
        canPublish={empresaValues.canPublish}
        isVerified={empresaValues.isVerified}
        memberSince={empresaValues.memberSince || empresaValues.created}
        publicationsUsed={sidebarMetrics.publications}
        teamSize={teamMembersCount}
        membershipRenewsAt={
          empresaValues.membershipRenewsAt ||
          empresaValues.subscriptionRenewsAt ||
          empresaValues.membershipRenewsAtRaw ||
          empresaValues.subscriptionRenewsAtRaw ||
          ''
        }
        variant={showUpgradeModal ? 'modal' : 'page'}
        onlySlugs={showUpgradeModal ? ['pro', 'premium', 'elite'] : null}
        onClose={showUpgradeModal ? () => handleSectionChange(fallbackSection) : undefined}
      />
    );
  } else if (currentSection === 'support' || currentSection === 'support-tickets') {
    content = (
      <SupportTicketsSection
        user={user}
        empresaId={empresaId}
        empresaOwnerId={empresaValues?.ownerId}
      />
    );
  } else if (currentSection === 'support-new') {
    content = (
      <SupportCreateTicketSection
        user={user}
        empresaId={empresaId}
      />
    );
  } else if (currentSection === 'feed') {
    content = (
      <FeedSection
        user={user}
        empresaValues={empresaValues}
        accountCard={sidebarAccountCard}
      />
    );
  } else if (currentSection === 'notificaciones') {
    content = <NotificationsSection notifications={companyNotifications} />;
  } else if (currentSection === 'estadisticas-reportes') {
    content = (
      <StatisticsReportsSection
        empresaValues={empresaValues}
        metrics={sidebarMetrics}
        activeRequests={activeRequestsCount}
        unreadNotifications={unreadNotificationsCount}
        isClient={isClient}
        isProvider={isProviderLike}
        profileViews={companyViewCount}
        canViewPrivateMetrics={isCompanyMember}
        isCompanyOwner={isCompanyOwner}
        visitorDetails={empresaProfileViews}
        canViewVisitorDetails={canViewVisitorDetails}
      />
    );
  } else if (currentSection === 'estadisticas-clientes') {
    content = (
      <StatisticsClientsSection
        empresaValues={empresaValues}
        metrics={sidebarMetrics}
        activeRequests={activeRequestsCount}
        unreadNotifications={unreadNotificationsCount}
        companyProfileViewTotal={empresaProfileViewsTotal || companyViewCount}
        companyProfileViewRecent={empresaProfileViews.length}
        userProfileViewTotal={userProfileViewsTotal}
        userProfileViewRecent={userProfileViews.length}
        publicationViewTotal={publicationViewTotal}
        publicationViewRecent={publicationViewDetails.length}
        teamSize={teamMembersCount}
      />
    );
  } else if (currentSection === 'estadisticas-proveedores') {
    content = (
      <StatisticsProvidersSection
        empresaValues={empresaValues}
        metrics={sidebarMetrics}
        activeRequests={activeRequestsCount}
        unreadNotifications={unreadNotificationsCount}
        companyProfileViewTotal={empresaProfileViewsTotal || companyViewCount}
        companyProfileViewRecent={empresaProfileViews.length}
        userProfileViewTotal={userProfileViewsTotal}
        userProfileViewRecent={userProfileViews.length}
        publicationViewTotal={publicationViewTotal}
        publicationViewRecent={publicationViewDetails.length}
        teamSize={teamMembersCount}
      />
    );
  } else {
    content = (
      <section className="dashboard-section dashboard-section--empty">
        <h1>{activeLabel}</h1>
        <p>Estamos preparando esta seccion. Vuelve pronto para gestionarla desde aqui.</p>
      </section>
    );
  }

  useEffect(() => {
    if (currentSection !== 'reservas' || typeof onCompanyNotificationsChange !== 'function') {
      return;
    }
    onCompanyNotificationsChange(prev => {
      if (!Array.isArray(prev) || prev.length === 0) {
        return prev;
      }
      let changed = false;
      const next = prev.map(item => {
        if (item?.type === 'reservation' && !item.read) {
          changed = true;
          return { ...item, read: true };
        }
        return item;
      });
      return changed ? next : prev;
    });
  }, [currentSection, onCompanyNotificationsChange]);

  const analyticsSections = ['estadisticas-clientes', 'estadisticas-proveedores'];
  const fullWidthSections = [
    'unidades',
    'flotilla',
    'fleet',
    'units',
    'estadisticas-reportes',
    'publicaciones',
    'promocionar',
    'reservas',
    'reservas-cliente',
    'solicitudes',
    'suscripciones',
    'support-tickets',
    'support-new',
    'notificaciones',
    'feed',
    ...analyticsSections,
  ];
  const isFullWidthLayout = fullWidthSections.includes(currentSection);
  const shouldHideSidebar =
    isFullWidthLayout ||
    currentSection === 'calendario' ||
    currentSection === 'empresa-verificacion' ||
    currentSection === 'notificaciones' ||
    currentSection === 'feed';

  const rootClassNames = ['dashboard-root'];
  if (isFullWidthLayout) {
    rootClassNames.push('dashboard-root--fullwidth');
  }
  if (currentSection) {
    rootClassNames.push(`dashboard-root--${currentSection}`);
  }

  const shouldRenderHelpCenterTabs =
    currentSection === 'support-tickets' || currentSection === 'support-new';
  const shouldRenderOperationsTabs = OPERATIONS_SECTIONS.has(currentSection);
  const shouldRenderAnalyticsTabs = ANALYTICS_SECTIONS.has(currentSection);
  const shouldRenderPromoteTabs = PROMOTE_SECTIONS.has(currentSection);

  return (
    <>
      {shouldRenderPromoteTabs ? <PromoteTabs /> : null}
      {shouldRenderOperationsTabs ? <OperationsTabs /> : null}
      {shouldRenderAnalyticsTabs ? <AnalyticsTabs /> : null}
      {shouldRenderHelpCenterTabs ? <HelpCenterTabs /> : null}
      <div className={rootClassNames.join(' ')}>
        {!shouldHideSidebar ? (
          <DashboardSidebar
            accountCard={sidebarAccountCard}
            navSections={sidebarNavSections}
            activeKey={currentSection}
            onNavigate={handleSectionChange}
          />
        ) : null}

        <div className="dashboard-content-wrapper">
          <main
            className={`dashboard-content${
              currentSection ? ` dashboard-content--${currentSection}` : ''
            }${isFullWidthLayout ? ' dashboard-content--fullwidth' : ''}`}
          >
            {content}
          </main>
        </div>

      </div>
    </>
  );
}
